// GAVION template attachments (supabase/schemas/66_gavion_template_attachments.sql): files stored in the private
// `template-attachments` bucket under `<template_id>/<uuid>.<ext>`, listed on email_templates.attachments. Admins add
// and remove them on the template page; the 1:1 compose downloads them when the rep picks the template.
import type { SupabaseClient } from "@supabase/supabase-js";
import { createContext } from "react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { IS_DEMO } from "../../env";

export const TEMPLATE_ATTACHMENTS_BUCKET = "template-attachments";
/** Same limits as the database check and the 1:1 send: 10 files, 10 MB in total. */
export const MAX_TEMPLATE_FILES = 10;
export const MAX_TEMPLATE_BYTES = 10 * 1024 * 1024;

export type TemplateAttachment = {
  path: string;
  name: string;
  contentType: string;
  size: number;
};

/** A safe extension for the object name (the original name is kept in the list). */
export const fileExtension = (name: string): string => {
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
  return /^[a-z0-9]{1,10}$/.test(ext) ? ext : "bin";
};

/** Storage path `<template_id>/<uuid>.<ext>` (the insert policy accepts nothing else). */
export const templateFilePath = (
  templateId: number | string,
  name: string,
  uuid: string = crypto.randomUUID(),
): string => `${templateId}/${uuid}.${fileExtension(name)}`;

/** A template's attachments as stored (anything malformed is dropped). */
export const templateAttachmentsOf = (v: unknown): TemplateAttachment[] =>
  Array.isArray(v)
    ? v.filter(
        (a): a is TemplateAttachment =>
          !!a &&
          typeof a.path === "string" &&
          typeof a.name === "string" &&
          typeof a.contentType === "string" &&
          typeof a.size === "number",
      )
    : [];

// eslint-disable-next-line no-control-regex -- control characters are exactly what is stripped
const UNSAFE_NAME_CHARS = /[\u0000-\u001f\u007f‎‏‪-‮⁦-⁩]/g;
/** The name kept in the list: no control or bidi character (the database refuses them), at most 200 characters. */
export const cleanFileName = (name: string): string =>
  Array.from(name.replace(UNSAFE_NAME_CHARS, "").trim())
    .slice(0, 200)
    .join("") || "fichier";

/** A MIME type the database accepts, else a generic one. */
const cleanContentType = (t: string): string =>
  /^[A-Za-z0-9.+-]+\/[A-Za-z0-9.+-]+$/.test(t) && t.length <= 100
    ? t
    : "application/octet-stream";

export type TemplateFileStore = {
  upload(templateId: number | string, file: File): Promise<TemplateAttachment>;
  /** Best effort: a leftover object is clutter, never sent. */
  remove(paths: string[]): Promise<void>;
  /** The file's content as base64 (for the send). */
  download(a: TemplateAttachment): Promise<string>;
};

export const TEMPLATE_FILE_FAILED = "template_file_failed";

const blobToBase64 = (b: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => {
      const s = String(r.result);
      resolve(s.slice(s.indexOf(",") + 1));
    };
    r.readAsDataURL(b);
  });

export const supabaseTemplateFileStore = (
  client: () => SupabaseClient,
): TemplateFileStore => {
  const bucket = () => client().storage.from(TEMPLATE_ATTACHMENTS_BUCKET);
  return {
    async upload(templateId, file) {
      const path = templateFilePath(templateId, file.name);
      const contentType = cleanContentType(file.type);
      const { error } = await bucket().upload(path, file, {
        contentType,
        upsert: false,
      });
      if (error) throw new Error(TEMPLATE_FILE_FAILED);
      return {
        path,
        name: cleanFileName(file.name),
        contentType,
        size: file.size,
      };
    },
    async remove(paths) {
      if (paths.length === 0) return;
      await bucket()
        .remove(paths)
        .catch(() => undefined);
    },
    async download(a) {
      const { data, error } = await bucket().download(a.path);
      if (error || !data) throw new Error(TEMPLATE_FILE_FAILED);
      return blobToBase64(data);
    },
  };
};

/** No Storage in the FakeRest demo, nor without a configured Supabase URL: template files are off. */
export const defaultTemplateFileStore: TemplateFileStore | null =
  IS_DEMO || !import.meta.env.VITE_SUPABASE_URL
    ? null
    : supabaseTemplateFileStore(getSupabaseClient);

/** Tests inject a store; null hides the template « Pièces jointes » and adds no files in the compose. */
export const TemplateFileStoreContext = createContext<TemplateFileStore | null>(
  defaultTemplateFileStore,
);
